import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Copy } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { getPaymentNote } from "@/i18n/helpers";
import { publicEventRequestConfig, type PublicEvent, type PublicGiftItem } from "@/services/api/publicEvents";
import type { HostSurfaceConfig } from "@/lib/event-templates";
import axios from "axios";
import { toast } from "sonner";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { getCookieConsent, hasPaymentCookieConsent, setCookieConsent } from "@/components/common/CookieConsentBanner";
import {
  contributionRecipientKey,
  getContributionRecipient,
  resolveGiftCardPaymentGate,
} from "@/utils/giftPayments";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://jubiel.local/api";
const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
const isStripeKeyValid =
  stripePublicKey &&
  !stripePublicKey.includes("CHANGE_ME") &&
  !stripePublicKey.includes("_ME") &&
  (stripePublicKey.startsWith("pk_test_") || stripePublicKey.startsWith("pk_live_"));

/**
 * Load Stripe only when needed; @stripe/stripe-js runs script injection on import so we dynamic-import.
 * JUB-53: direct charges (owner fee-bearer mode) live ON the owner's connected account —
 * Stripe.js must then be initialised with that account, otherwise confirmCardPayment fails.
 */
function loadStripeWhenConsented(
  stripeAccount?: string | null
): ReturnType<typeof import("@stripe/stripe-js").loadStripe> {
  return import("@stripe/stripe-js").then((m) =>
    m.loadStripe(stripePublicKey, stripeAccount ? { stripeAccount } : undefined)
  );
}

interface PublicGiftContributionFormProps {
  event: PublicEvent;
  gift: PublicGiftItem;
  config: HostSurfaceConfig;
  locale: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /**
   * JUB-53: from the public gifts payload. Explicit false means the event owner
   * has no charges-enabled connected Stripe account — the card pay flow is
   * replaced with the informational note (envelope path stays valid).
   */
  cardPaymentsEnabled?: boolean;
  /** JUB-53: connected account for Stripe.js init (direct-charge mode only). */
  stripeAccountId?: string | null;
}

type FormErrors = {
  amount?: string;
  name?: string;
  email?: string;
  message?: string;
  general?: string;
  consent?: string;
};

const RATE_LIMIT_KEY = "gift_contribution_attempts";
const RATE_LIMIT_MAX = 20; // Soft guard - warning only
const RATE_LIMIT_WINDOW = 60000; // 1 minute

export function PublicGiftContributionForm({
  event,
  gift,
  config: _config,
  locale: _locale,
  open,
  onOpenChange,
  onSuccess,
  cardPaymentsEnabled,
  stripeAccountId,
}: PublicGiftContributionFormProps) {
  const hasConsent = hasPaymentCookieConsent();
  const [stripePromise, setStripePromise] = useState<Awaited<ReturnType<typeof loadStripeWhenConsented>> | null>(null);
  const [, forceUpdate] = useState(0);

  // JUB-53: only an explicit false gates the card flow (undefined = payload
  // without the flag, e.g. authenticated draft previews — keep legacy behavior).
  const { cardPaymentsAllowed, stripeAccount } = resolveGiftCardPaymentGate({
    cardPaymentsEnabled,
    stripeAccountId,
  });

  useEffect(() => {
    if (!hasConsent || !isStripeKeyValid || !cardPaymentsAllowed || stripePromise) return;
    loadStripeWhenConsented(stripeAccount).then(setStripePromise);
  }, [hasConsent, stripePromise, cardPaymentsAllowed, stripeAccount]);

  useEffect(() => {
    const onConsent = () => {
      if (hasPaymentCookieConsent() && isStripeKeyValid && cardPaymentsAllowed) {
        setStripePromise((prev) => {
          if (prev) return prev;
          loadStripeWhenConsented(stripeAccount).then(setStripePromise);
          return prev;
        });
      }
    };
    window.addEventListener("cookieConsentUpdated", onConsent);
    return () => window.removeEventListener("cookieConsentUpdated", onConsent);
  }, [cardPaymentsAllowed, stripeAccount]);

  // PublicGiftContributionFormInner calls useStripe()/useElements() unconditionally,
  // so EVERY render path must be inside an <Elements> provider — rendering it bare
  // throws "Could not find Elements context" and white-screens the public page.
  // <Elements stripe={null}> is valid while Stripe hasn't loaded; the stripe prop
  // must never change on a mounted instance, so key remounts when it resolves.
  const stripeLocale = (_locale === 'ro' || _locale === 'en') ? _locale : 'ro';

  let inner: ReactNode;
  if (!isStripeKeyValid || !cardPaymentsAllowed) {
    // No Stripe key OR owner not Stripe-connected (JUB-53 gate): no card form —
    // the informational note + pledge path renders instead.
    inner = (
      <PublicGiftContributionFormInner
        event={event}
        gift={gift}
        config={_config}
        locale={_locale}
        open={open}
        onOpenChange={onOpenChange}
        onSuccess={onSuccess}
        stripeEnabled={false}
      />
    );
  } else if (!hasConsent) {
    inner = (
      <PublicGiftContributionFormWithConsentGate
        event={event}
        gift={gift}
        config={_config}
        locale={_locale}
        open={open}
        onOpenChange={onOpenChange}
        onSuccess={onSuccess}
        onAcceptAndContinue={() => {
          const existing = getCookieConsent();
          setCookieConsent({
            essential: true,
            analytics: existing?.analytics ?? false,
            marketing: existing?.marketing ?? false,
            timestamp: Date.now(),
          });
          forceUpdate((n) => n + 1);
        }}
      />
    );
  } else {
    inner = (
      <PublicGiftContributionFormInner
        event={event}
        gift={gift}
        config={_config}
        locale={_locale}
        open={open}
        onOpenChange={onOpenChange}
        onSuccess={onSuccess}
        stripeEnabled={!!stripePromise}
      />
    );
  }

  return (
    <Elements
      // Keyed remount when Stripe resolves AND when the account context differs:
      // the stripe prop must never change on a mounted Elements instance, and a
      // connected-account instance cannot confirm platform PaymentIntents.
      key={`${stripeAccount ?? 'platform'}-${stripePromise ? 'stripe-ready' : 'stripe-pending'}`}
      stripe={stripePromise}
      options={{ locale: stripeLocale }}
    >
      {inner}
    </Elements>
  );
}

function PublicGiftContributionFormWithConsentGate(
  props: PublicGiftContributionFormProps & { onAcceptAndContinue: () => void }
) {
  const { t } = useTranslation();
  const { event, gift, config, locale, open, onOpenChange, onSuccess, onAcceptAndContinue } = props;
  return (
    <PublicGiftContributionFormInner
      event={event}
      gift={gift}
      config={config}
      locale={locale}
      open={open}
      onOpenChange={onOpenChange}
      onSuccess={onSuccess}
      stripeEnabled={false}
      paymentConsentMessage={
        <>
          <p className="text-sm text-muted-foreground mb-3">{t("cookies.paymentConsent.contributionMessage")}</p>
          <Button onClick={onAcceptAndContinue}>{t("cookies.paymentConsent.acceptAndContinue")}</Button>
        </>
      }
    />
  );
}

function PublicGiftContributionFormInner({
  event,
  gift,
  config: _config,
  locale: _locale,
  open,
  onOpenChange,
  onSuccess,
  stripeEnabled,
  paymentConsentMessage,
}: PublicGiftContributionFormProps & { stripeEnabled: boolean; paymentConsentMessage?: ReactNode }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // JUB-105: GDPR consent — required before the contribution can be submitted.
  const [consent, setConsent] = useState(false);
  const noticeLocale = _locale === "ro" || _locale === "en" ? _locale : "ro";
  const noticePath = `/${noticeLocale}/data-processing-notice`;
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // JUB-53 guest fee-bearer mode: the payment-intent endpoint returned a
  // processing fee that must be shown BEFORE the card payment is confirmed.
  // The first submit stores it here; the second submit confirms the payment.
  const [pendingFeeConfirm, setPendingFeeConfirm] = useState<{
    clientSecret: string;
    chargeId: string;
    fee: number;
    total: number;
  } | null>(null);
  // JUB-79: persistent post-payment confirmation state — the dialog switches
  // to a proof view (amount, gift, recipient, reference code) instead of a
  // transient toast, and does NOT auto-close.
  const [confirmation, setConfirmation] = useState<{
    amount: number;
    currency: string;
    giftName: string;
    reference: string;
    email: string;
    emailSent: boolean;
  } | null>(null);
  const [referenceCopied, setReferenceCopied] = useState(false);

  // JUB-79: recipient identity — WHO receives the money (Nicu pilot:
  // "eu virez o mie de lei către… cine, exact?").
  const recipient = getContributionRecipient(event);
  const recipientLine = t(contributionRecipientKey(recipient.kind), { names: recipient.names });

  const isCashFund = !!gift.cash;

  // Calculate remaining amount (only applies to non-cash gifts)
  const remainingAmount = useMemo(() => {
    if (isCashFund) return Number.POSITIVE_INFINITY;
    return Math.max(0, gift.amount - (gift.totalContributions || 0));
  }, [gift.amount, gift.totalContributions, isCashFund]);

  // Rate limiting check
  const checkRateLimit = (): boolean => {
    try {
      const stored = sessionStorage.getItem(RATE_LIMIT_KEY);
      const data = stored ? JSON.parse(stored) : { count: 0, resetAt: Date.now() + RATE_LIMIT_WINDOW };

      if (Date.now() > data.resetAt) {
        data.count = 0;
        data.resetAt = Date.now() + RATE_LIMIT_WINDOW;
      }

      if (data.count >= RATE_LIMIT_MAX) {
        setRateLimitWarning(true);
        return true; // Soft guard - allow but warn
      }

      data.count++;
      sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
      return false;
    } catch {
      return false;
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Amount validation
    const amountNum = parseFloat(amount);
    // NOTE(JUB-132): this used to gate on `config?.public?.enforceRemainingAmount`,
    // but EventTemplateConfig has never had a `public` field (no caller ever sets
    // one), so that predicate was always false and these two branches were dead
    // code. Removed rather than left unreachable; see shared-type recommendation
    // if remaining-amount enforcement should actually be wired up.
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount = t("validation.amountRequired");
    } else if (amountNum < 0.01) {
      newErrors.amount = t("validation.amountTooSmall");
    }

    // Name validation
    if (!name || !name.trim()) {
      newErrors.name = t("validation.nameRequired");
    }

    // Email validation
    if (!email) {
      newErrors.email = t("validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("validation.emailInvalid");
    }

    // JUB-105: GDPR consent required before submitting.
    if (!consent) {
      newErrors.consent = t("validation.consentRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Check rate limit
    checkRateLimit();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const eventIdentifier = (event.url || event.uuid).replace(/^@/, "");
      if (!stripeEnabled) {
        // Fallback: create contribution record only (no card payment)
        const response = await axios.post(
        `${API_BASE_URL}/events/public/${eventIdentifier}/gifts/${gift.id}/contribute`,
          {
            amount: parseFloat(amount),
            name: name.trim(),
            email: email.trim(),
            message: message.trim() || undefined,
            // JUB-105: GDPR consent, recorded server-side with a version + timestamp.
            consent,
          },
          {
            headers: {
              ...publicEventRequestConfig(eventIdentifier).headers,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data) {
          toast.success(
            t("gifts.contributionSubmitted")
          );
          setAmount("");
          setName("");
          setEmail("");
          setMessage("");
          setConsent(false);
          setErrors({});
          onOpenChange(false);
          onSuccess?.();
        }
        return;
      }

      if (!stripe || !elements) {
        setErrors({
          general: t("payment.stripeNotReady"),
        });
        return;
      }

      setPaymentIntentLoading(true);

      // Ensure amount is a valid number
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        setErrors({
          amount: t("validation.amountRequired"),
        });
        setPaymentIntentLoading(false);
        return;
      }

      let secret: string;
      let chargeId: string | undefined;

      if (pendingFeeConfirm) {
        // Second click: guest acknowledged the processing fee — confirm the
        // PaymentIntent that was already created.
        secret = pendingFeeConfirm.clientSecret;
        chargeId = pendingFeeConfirm.chargeId;
      } else {
        const paymentIntentResponse = await axios.post(
          `${API_BASE_URL}/events/public/${eventIdentifier}/gifts/${gift.id}/payment-intent`,
          {
            amount: amountNum,
            name: name.trim(),
            email: email.trim(),
            message: message.trim() || undefined,
            // JUB-105: GDPR consent, recorded server-side with a version + timestamp.
            consent,
          },
          {
            headers: {
              ...publicEventRequestConfig(eventIdentifier).headers,
              "Content-Type": "application/json",
            },
          }
        );

        secret = paymentIntentResponse.data?.clientSecret;
        chargeId = paymentIntentResponse.data?.chargeId;
        if (!secret) {
          setErrors({
            general: t("payment.intentError", {
            }),
          });
          return;
        }

        setClientSecret(secret);

        // JUB-53 guest mode: a processing fee is added on top of the entered
        // amount. Show it and require an explicit second confirmation click.
        const processingFee = Number(paymentIntentResponse.data?.processingFee ?? 0);
        const totalAmount = Number(paymentIntentResponse.data?.totalAmount ?? amountNum);
        if (processingFee > 0 && chargeId) {
          setPendingFeeConfirm({
            clientSecret: secret,
            chargeId,
            fee: processingFee,
            total: totalAmount,
          });
          return;
        }
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setErrors({
          general: t("payment.cardUnavailable", {
          }),
        });
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(secret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error || !paymentIntent) {
        setErrors({
          general:
            error?.message ||
            t("payment.error"),
        });
        return;
      }

      let platformEmailSent = false;
      if (chargeId) {
        const confirmResponse = await axios.post(
          `${API_BASE_URL}/events/public/${eventIdentifier}/charges/${chargeId}/confirm`,
          {},
          {
            headers: {
              ...publicEventRequestConfig(eventIdentifier).headers,
              "Content-Type": "application/json",
            },
          }
        );
        platformEmailSent = confirmResponse.data?.emailSent === true;
      }

      // JUB-79: switch the dialog to the persistent confirmation view instead
      // of a transient toast — the guest keeps a reference code on screen.
      setConfirmation({
        amount: amountNum,
        currency: gift.currency || "RON",
        giftName: gift.name,
        reference: chargeId || "",
        email: email.trim(),
        emailSent: platformEmailSent,
      });
      setAmount("");
      setName("");
      setEmail("");
      setMessage("");
      setErrors({});
      setClientSecret(null);
      setPendingFeeConfirm(null);
      // NOTE: onSuccess is deliberately NOT called here — a caller may close
      // the dialog in onSuccess, which would destroy the persistent
      // confirmation view. It fires when the guest closes it.
    } catch (error: unknown) {
      console.error("Gift contribution submission error:", error);
      const maybeAxiosError = error as {
        response?: {
          data?: {
            error?: { message?: string; code?: string };
            // Laravel's default ValidationException JSON shape: top-level
            // `errors`, not nested under `error` like ApiErrorResponse.
            errors?: Record<string, string | string[]>;
          };
          status?: number;
        };
        message?: string;
      };
      const errorMessage =
        maybeAxiosError.response?.data?.error?.message ||
        maybeAxiosError.response?.data?.error?.code ||
        maybeAxiosError.message ||
        t("gifts.contributionError");

      if (maybeAxiosError.response?.status === 422) {
        // Validation errors
        const validationErrors = maybeAxiosError.response.data?.errors || {};
        const newErrors: FormErrors = {};

        if (validationErrors.amount) {
          newErrors.amount = Array.isArray(validationErrors.amount) ? validationErrors.amount[0] : validationErrors.amount;
        }
        if (validationErrors.name) {
          newErrors.name = Array.isArray(validationErrors.name) ? validationErrors.name[0] : validationErrors.name;
        }
        if (validationErrors.email) {
          newErrors.email = Array.isArray(validationErrors.email) ? validationErrors.email[0] : validationErrors.email;
        }
        if (validationErrors.message) {
          newErrors.message = Array.isArray(validationErrors.message) ? validationErrors.message[0] : validationErrors.message;
        }

        setErrors(newErrors);
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setPaymentIntentLoading(false);
      setLoading(false);
    }
  };

  // Clear error when field changes
  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
    // Any edit invalidates a fee quote pending confirmation (JUB-53 guest mode):
    // the PaymentIntent was created for the previous values.
    if (pendingFeeConfirm) {
      setPendingFeeConfirm(null);
    }
  };

  // Check if form is valid
  const isFormValid = useMemo(() => {
    const amountNum = parseFloat(amount);
    if (
      !amount ||
      isNaN(amountNum) ||
      amountNum <= 0 ||
      (!isCashFund && remainingAmount > 0 && amountNum > remainingAmount)
    )
      return false;
    if (!name || !name.trim()) return false;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    // JUB-105: GDPR consent required before submitting.
    if (!consent) return false;
    return true;
  }, [amount, consent, email, isCashFund, name, remainingAmount]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setAmount("");
      setName("");
      setEmail("");
      setMessage("");
      setConsent(false);
      setErrors({});
      setRateLimitWarning(false);
      setClientSecret(null);
      setPendingFeeConfirm(null);
      setConfirmation(null);
      setReferenceCopied(false);
    }
  }, [open]);

  const handleCopyReference = async () => {
    if (!confirmation?.reference) return;
    try {
      await navigator.clipboard.writeText(confirmation.reference);
      setReferenceCopied(true);
      toast.success(t("gifts.confirmation.referenceCopied"));
    } catch {
      // Clipboard unavailable (permissions/insecure context) — the full code
      // stays visible in the view so the guest can still select it manually.
    }
  };

  // JUB-79: persistent confirmation view (does NOT auto-close). onSuccess is
  // deferred until the guest dismisses it — templates may close/refresh there.
  const closeConfirmation = () => {
    onSuccess?.();
    onOpenChange(false);
  };

  if (confirmation) {
    return (
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeConfirmation();
          }
        }}
      >
        <DialogContent className="w-[90vw] max-w-lg" data-testid="gift-contribution-confirmation">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
              {t("gifts.confirmation.title")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t("gifts.confirmation.amountLabel")}</span>
                <span className="font-semibold" data-testid="confirmation-amount">
                  {confirmation.amount.toFixed(2)} {confirmation.currency}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t("gifts.confirmation.giftLabel")}</span>
                <span className="font-medium text-right">{confirmation.giftName}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t("gifts.confirmation.recipientLabel")}</span>
                <span className="font-medium text-right">{recipient.names}</span>
              </div>
            </div>

            <div className="p-4 border rounded-lg space-y-2">
              <p className="text-sm text-muted-foreground">{t("gifts.confirmation.referenceLabel")}</p>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-lg font-semibold" data-testid="confirmation-reference">
                  {confirmation.reference.slice(0, 8)}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyReference}
                  data-testid="confirmation-copy-reference"
                >
                  <Copy className="h-4 w-4 mr-2" aria-hidden="true" />
                  {referenceCopied ? t("gifts.confirmation.referenceCopied") : t("gifts.confirmation.copyReference")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground font-mono break-all">{confirmation.reference}</p>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              <p>{t("gifts.confirmation.processorEmailNote")}</p>
              {confirmation.emailSent && confirmation.email && (
                <p>{t("gifts.confirmation.platformEmailNote", { email: confirmation.email })}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={closeConfirmation} data-testid="confirmation-close">
              {t("gifts.confirmation.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("gifts.contributeToGift", { giftName: gift.name })}</DialogTitle>
        </DialogHeader>

        {rateLimitWarning && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("gifts.rateLimitWarning")}
            </AlertDescription>
          </Alert>
        )}

        {errors.general && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}

        {paymentConsentMessage}
        {!stripeEnabled && !paymentConsentMessage && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("payment.stripeNotConfigured")}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-6 py-4">
          {/* Gift Information */}
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            <h3 className="font-semibold">{gift.name}</h3>
            {gift.description && <p className="text-sm text-muted-foreground">{gift.description}</p>}
            <div className="text-sm text-muted-foreground">
              {t("gifts.totalAmount")}: {gift.amount} {gift.currency || "RON"}
            </div>
          </div>

          {/* Contribution Form */}
          <div className="space-y-4">
            <h3 className="font-semibold">{t("gifts.contributionDetails")}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="amount">
                  {t("gifts.amount")} ({gift.currency || "RON"}) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={isCashFund ? undefined : remainingAmount}
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    clearError("amount");
                  }}
                  className={errors.amount ? "border-destructive" : ""}
                  placeholder={t("gifts.amountPlaceholder")}
                />
                {errors.amount && <p className="text-sm text-destructive mt-1">{errors.amount}</p>}
                {/* JUB-80: surface the cap BEFORE amount entry instead of a
                    raw untranslated 'Max: N' revealed only as a placeholder */}
                {!isCashFund && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("gifts.remainingAmount", {
                      amount: remainingAmount.toFixed(2),
                      currency: gift.currency || "RON",
                    })}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="name">
                  {t("gifts.contributorName")} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError("name");
                  }}
                  className={errors.name ? "border-destructive" : ""}
                  placeholder={t("gifts.contributorNamePlaceholder")}
                />
                {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="email">
                  {t("gifts.email")} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError("email");
                  }}
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="message">{t("gifts.message")}</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  clearError("message");
                }}
                className={errors.message ? "border-destructive" : ""}
                rows={4}
                placeholder={t("gifts.messagePlaceholder")}
              />
              {errors.message && <p className="text-sm text-destructive mt-1">{errors.message}</p>}
            </div>

            {/* JUB-105: GDPR consent — required before submitting */}
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                id="gift-consent"
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  if (errors.consent) {
                    setErrors((prev) => ({ ...prev, consent: undefined }));
                  }
                }}
                className="mt-1 rounded border-gray-300"
                data-testid="gift-consent-checkbox"
              />
              <Label htmlFor="gift-consent" className="font-normal leading-relaxed">
                {t("gifts.consent.text")}{" "}
                <Link to={noticePath} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {t("gifts.consent.link")}
                </Link>
                .
              </Label>
            </div>
            {errors.consent && (
              <p className="text-sm text-destructive mt-1">{errors.consent}</p>
            )}
          </div>

          {/* JUB-79: recipient identity — shown above the card element so the
              guest knows WHO receives the money before paying. */}
          <div
            className="text-sm font-medium p-3 bg-muted/30 border rounded-lg"
            data-testid="gift-recipient-line"
          >
            {recipientLine}
          </div>

          {stripeEnabled && (
            <div className="space-y-2">
              <h3 className="font-semibold">{t("payment.paymentDetails")}</h3>
              <div className="rounded-md border bg-background px-3 py-4">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#111827",
                        "::placeholder": {
                          color: "#9ca3af",
                        },
                      },
                      invalid: {
                        color: "#ef4444",
                      },
                    },
                  }}
                />
              </div>
              {paymentIntentLoading && (
                <p className="text-sm text-muted-foreground">
                  {t("payment.intentLoading")}
                </p>
              )}
              {pendingFeeConfirm && (
                <div
                  className="rounded-md border bg-muted/30 px-3 py-3 text-sm space-y-1"
                  data-testid="gift-processing-fee-notice"
                >
                  <p className="text-muted-foreground">
                    {t("payment.processingFeeLine", {
                      fee: pendingFeeConfirm.fee.toFixed(2),
                      currency: gift.currency || "RON",
                    })}
                  </p>
                  <p className="font-semibold">
                    {t("payment.totalWithFee", {
                      total: pendingFeeConfirm.total.toFixed(2),
                      currency: gift.currency || "RON",
                    })}
                  </p>
                </div>
              )}
              {clientSecret && import.meta.env.DEV && (
                <p className="text-xs text-muted-foreground break-all">
                  clientSecret: {clientSecret}
                </p>
              )}
            </div>
          )}

          <div className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            <p>
              {getPaymentNote(t, { stripeEnabled })}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || loading || (stripeEnabled && (!stripe || !elements))}>
            {loading || paymentIntentLoading
              ? t("common.loading")
              : pendingFeeConfirm
                ? t("payment.confirmFeeCta", {
                    total: pendingFeeConfirm.total.toFixed(2),
                    currency: gift.currency || "RON",
                  })
                : stripeEnabled
                  ? t("payment.submitCta")
                  : t("gifts.submitContribution")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

